import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import jsPDF from 'jspdf';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Clock3,
  Cpu,
  Download,
  FileText,
  Gauge,
  Lock,
  RefreshCw,
  Satellite,
  ShieldCheck,
  SlidersHorizontal,
  Waves,
} from 'lucide-react';

const loes = [
  {
    id: 'loe1',
    label: 'LoE 1',
    name: 'Quantum Magnetometers',
    subtitle: 'Electromagnetic anomaly awareness for maritime and subsurface ISR',
    mission: 'Magnetic-anomaly and electromagnetic-signature scenario scoring for anti-submarine, mine-countermeasure, and littoral surveillance contexts.',
    objective: 'Compare a candidate quantum magnetometer payload against classical limits, SWaP constraints, platform noise, and transition evidence.',
    riskDrivers: ['Platform EMI', 'Motion noise', 'Marine clutter', 'Calibration drift'],
    salarEdge: 'SALAR wraps the sensor candidate with a decision trace: mission scenario, SWaP evidence, interface assumptions, supplier confidence, and test-plan readiness.',
    baseline: 42,
    advantage: 74,
  },
  {
    id: 'loe2',
    label: 'LoE 2',
    name: 'Quantum Gravimeters',
    subtitle: 'Gravity-anomaly evidence model for hidden mass / void detection',
    mission: 'Subsurface anomaly scoring for tunnel, bunker, infrastructure, seabed, or survey scenarios where classical sensors face vibration and drift limits.',
    objective: 'Score mobile gravimeter candidates against dynamic platform conditions, environmental disturbance, and field-test readiness.',
    riskDrivers: ['Vibration', 'Thermal drift', 'Motion compensation', 'Reference-map quality'],
    salarEdge: 'SALAR separates sensor promise from test evidence, integration risk, environmental assumptions, and transition gaps.',
    baseline: 38,
    advantage: 68,
  },
  {
    id: 'loe3',
    label: 'LoE 3',
    name: 'Portable Quantum Clocks',
    subtitle: 'Resilient timing for GNSS-denied ISR and sensor-fusion operations',
    mission: 'Portable timing and synchronization scenario scoring for communications, coherent sensing, distributed ISR payloads, and contested PNT operations.',
    objective: 'Compare clock concepts against SWaP, warm-up time, interface outputs, environmental ruggedness, and sensor-fusion mission impact.',
    riskDrivers: ['Warm-up time', 'Power draw', 'Acceleration sensitivity', 'Interface compatibility'],
    salarEdge: 'SALAR frames quantum clock candidates as mission-continuity components with timing evidence, PNT-denied use case, and integration outputs tracked together.',
    baseline: 46,
    advantage: 71,
  },
  {
    id: 'loe4',
    label: 'LoE 4',
    name: 'Component Technologies',
    subtitle: 'Recommended SALAR lead: component, provenance, interface, and transition evidence twin',
    mission: 'Component-technology decision support across chip-scale optics, vapor-cell subsystems, packaging, photonics, timing interfaces, sensor electronics, and trusted supply paths.',
    objective: 'Provide a TRL-4 evidence-model prototype that helps reviewers compare component technologies across SWaP-C, provenance, manufacturability, and mission fit.',
    riskDrivers: ['Single-source components', 'Packaging yield', 'Interface mismatch', 'Evidence gaps'],
    salarEdge: 'This is SALAR’s safest FARSEER entry point: a component-technology assurance layer that supports LoE 1-3 without claiming ownership of physical quantum sensor hardware.',
    baseline: 51,
    advantage: 82,
  },
];

const scenarios = [
  { id: 'maritime', label: 'Maritime ISR', icon: Waves },
  { id: 'airborne', label: 'Airborne Payload', icon: Satellite },
  { id: 'pnt', label: 'GNSS-Denied PNT', icon: Clock3 },
  { id: 'component', label: 'Component Transition', icon: Cpu },
];

const pillars = [
  ['P1', 'Board reliability / payload electronics'],
  ['P3', 'Supply-chain graph and source confidence'],
  ['P4', 'Manufacturing stability and pilot evidence'],
  ['P6/P7', 'Infrastructure and provenance decision trace'],
];

function computeScore({ loe, scenario, noise, swap, provenance }) {
  const scenarioBoost = {
    maritime: loe.id === 'loe1' ? 8 : loe.id === 'loe4' ? 4 : 1,
    airborne: loe.id === 'loe2' || loe.id === 'loe4' ? 6 : 2,
    pnt: loe.id === 'loe3' ? 9 : loe.id === 'loe4' ? 4 : 1,
    component: loe.id === 'loe4' ? 10 : 3,
  }[scenario];
  const riskPenalty = Math.round((noise * 0.22) + (swap * 0.14) + ((100 - provenance) * 0.18));
  const raw = loe.advantage + scenarioBoost - riskPenalty;
  return Math.max(18, Math.min(96, raw));
}

function scoreBand(score) {
  if (score >= 80) return { label: 'High fit', color: 'text-emerald-300', bar: 'bg-emerald-400' };
  if (score >= 60) return { label: 'Good fit / needs evidence', color: 'text-cyan-300', bar: 'bg-cyan-400' };
  if (score >= 45) return { label: 'Watch list', color: 'text-amber-300', bar: 'bg-amber-400' };
  return { label: 'Gap / not ready', color: 'text-red-300', bar: 'bg-red-400' };
}

function StatBar({ label, value, icon: Icon }) {
  const band = scoreBand(value);
  return (
    <div className="rounded-xl border border-cyan-400/20 bg-slate-950/50 p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-blue-100">
          {Icon && <Icon size={16} className="text-cyan-300" />}
          {label}
        </div>
        <span className={`text-sm font-black ${band.color}`}>{value}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${band.bar}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function Slider({ label, value, setValue, low, high }) {
  return (
    <label className="block rounded-xl border border-blue-500/20 bg-slate-950/40 p-4">
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="font-semibold text-blue-100">{label}</span>
        <span className="font-mono text-cyan-300">{value}</span>
      </div>
      <input
        type="range"
        min={low}
        max={high}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full accent-cyan-400"
      />
    </label>
  );
}

function wrapText(doc, text, x, y, width, lineHeight = 6) {
  const lines = doc.splitTextToSize(text, width);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

export default function QuantumSensingISR() {
  const [activeLoe, setActiveLoe] = useState(loes[3]);
  const [scenario, setScenario] = useState('component');
  const [noise, setNoise] = useState(35);
  const [swap, setSwap] = useState(32);
  const [provenance, setProvenance] = useState(76);

  const score = useMemo(() => computeScore({ loe: activeLoe, scenario, noise, swap, provenance }), [activeLoe, scenario, noise, swap, provenance]);
  const band = scoreBand(score);
  const trl = score >= 75 ? 'TRL 4 software/evidence-model prototype' : 'TRL 3-4 concept-to-breadboard evidence model';
  const riskScore = Math.max(8, 100 - score);
  const transition = Math.min(96, Math.round((score * 0.72) + (provenance * 0.2) - (swap * 0.04)));

  const downloadReport = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    doc.setFillColor(5, 18, 34);
    doc.rect(0, 0, 210, 297, 'F');
    doc.setTextColor(6, 220, 205);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('SALAR Farseer-Q: Quantum ISR Evidence Twin', 16, 22);
    doc.setFontSize(10);
    doc.setTextColor(210, 236, 255);
    doc.text('SALAR Technologies LLC | Founder & Principal: Vikrant Salar | salartechnologies.com', 16, 30);
    doc.setDrawColor(6, 220, 205);
    doc.line(16, 36, 194, 36);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Demonstration Summary', 16, 50);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(215, 235, 255);
    let y = 58;
    y = wrapText(doc, `Selected Line of Effort: ${activeLoe.label} - ${activeLoe.name}`, 16, y, 178);
    y = wrapText(doc, `Scenario: ${scenarios.find((s) => s.id === scenario)?.label || scenario}`, 16, y + 3, 178);
    y = wrapText(doc, `TRL posture: ${trl}. Recommended claim is TRL 4 for the software/evidence-model demo only, not for physical quantum sensor hardware.`, 16, y + 3, 178);
    y = wrapText(doc, `Mission-fit score: ${score}/100 (${band.label}). Risk score: ${riskScore}. Transition evidence score: ${transition}.`, 16, y + 3, 178);
    y = wrapText(doc, `Noise stress: ${noise}. SWaP stress: ${swap}. Provenance confidence: ${provenance}.`, 16, y + 3, 178);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(6, 220, 205);
    doc.text('Public-Safe Demonstration Boundary', 16, y + 10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(215, 235, 255);
    y = wrapText(doc, 'This report uses synthetic public demonstration values only. It contains no classified information, no CUI, no GFI, no export-controlled implementation details, and no government endorsement claim.', 16, y + 18, 178);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(6, 220, 205);
    doc.text('SALAR Evidence Pillars', 16, y + 10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(215, 235, 255);
    pillars.forEach(([id, text]) => {
      y = wrapText(doc, `${id}: ${text}`, 16, y + 18, 178);
    });

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(6, 220, 205);
    doc.text('Invention Name', 16, y + 10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(215, 235, 255);
    wrapText(doc, 'SALAR Farseer-Q: Quantum ISR Evidence Twin', 16, y + 18, 178);

    doc.setFontSize(8);
    doc.setTextColor(140, 180, 210);
    doc.text('Generated from the public SALAR /quantum-sensing-for-isr demo page.', 16, 286);
    doc.save('SALAR_Farseer_Q_Demo_Report.pdf');
  };

  return (
    <div className="min-h-screen bg-[#06111f] text-white overflow-hidden" data-testid="quantum-sensing-isr-page">
      <Helmet>
        <title>Farseer-Q Quantum ISR Evidence Twin | SALAR Technologies LLC</title>
        <meta name="description" content="Public-safe SALAR Farseer-Q demo for DIU FARSEER quantum sensing ISR: LoE comparison, TRL posture, component evidence scoring, and downloadable demonstration report." />
        <link rel="canonical" href="https://www.salartechnologies.com/quantum-sensing-for-isr" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="relative border-b border-cyan-400/20 bg-[radial-gradient(circle_at_top_right,rgba(20,184,166,.22),transparent_40%),linear-gradient(135deg,#071426_0%,#0a1628_55%,#020617_100%)]">
        <div className="container mx-auto px-5 sm:px-6 py-12 lg:py-16">
          <div className="grid lg:grid-cols-[1.08fr_.92fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.22em] text-cyan-200 mb-5">
                <ShieldCheck size={15} /> Public-safe demo · synthetic examples only
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight tracking-tight">
                SALAR Farseer-Q:<br />
                <span className="text-cyan-300">Quantum ISR Evidence Twin</span>
              </h1>
              <p className="mt-5 max-w-3xl text-base sm:text-lg text-blue-100 leading-relaxed">
                A responsive demonstration for the FARSEER quantum sensing mission area. It compares Lines of Effort, exposes SWaP-C and evidence gaps, and creates a structured decision trace for quantum sensing, timing, and component-technology transition review.
              </p>
              <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-3xl">
                <StatBar label="Current posture" value={64} icon={Gauge} />
                <StatBar label="LoE 4 fit" value={82} icon={Cpu} />
                <StatBar label="Evidence export" value={90} icon={FileText} />
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <button onClick={downloadReport} className="inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-4 py-3 text-sm font-black text-[#06111f] hover:bg-cyan-300 transition-colors">
                  <Download size={17} /> Download demo PDF
                </button>
                <a href="#loe-explorer" className="inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-3 text-sm font-bold text-cyan-100 hover:bg-cyan-400/20 transition-colors">
                  <SlidersHorizontal size={17} /> Open LoE explorer
                </a>
              </div>
            </div>

            <div className="rounded-3xl border border-cyan-400/25 bg-[#091827]/85 shadow-2xl shadow-cyan-950/40 p-5 sm:p-6 backdrop-blur">
              <div className="flex items-center justify-between gap-3 mb-5">
                <div>
                  <p className="text-xs font-bold tracking-[0.22em] uppercase text-cyan-300">TRL posture</p>
                  <h2 className="text-2xl font-black mt-1">{trl}</h2>
                </div>
                <div className={`rounded-full border border-cyan-300/30 px-3 py-1 text-sm font-black ${band.color}`}>{band.label}</div>
              </div>
              <div className="rounded-2xl border border-blue-500/20 bg-slate-950/45 p-5">
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-blue-100 font-semibold">Mission-fit score</span>
                  <span className="text-3xl font-black text-cyan-300">{score}</span>
                </div>
                <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
                  <div className={`h-full ${band.bar}`} style={{ width: `${score}%` }} />
                </div>
                <p className="mt-4 text-sm text-blue-100 leading-relaxed">
                  Recommendation: lead with <strong className="text-white">LoE 4 Component Technologies</strong>. Treat LoE 1-3 as supported mission scenarios until physical quantum sensor or clock data is available.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="rounded-xl bg-slate-950/50 border border-cyan-400/15 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-blue-300">Risk</p>
                  <p className="text-xl font-black text-amber-300">{riskScore}</p>
                </div>
                <div className="rounded-xl bg-slate-950/50 border border-cyan-400/15 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-blue-300">Transition</p>
                  <p className="text-xl font-black text-cyan-300">{transition}</p>
                </div>
                <div className="rounded-xl bg-slate-950/50 border border-cyan-400/15 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-blue-300">LoE</p>
                  <p className="text-xl font-black text-emerald-300">{activeLoe.label}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="loe-explorer" className="py-12 lg:py-16 bg-[#081525]">
        <div className="container mx-auto px-5 sm:px-6">
          <div className="max-w-4xl mb-8">
            <p className="text-xs font-black tracking-[0.22em] uppercase text-cyan-300 mb-2">Lines of Effort Explorer</p>
            <h2 className="text-3xl lg:text-4xl font-black">Select an LoE, stress the mission, and export the result.</h2>
            <p className="mt-3 text-blue-100 leading-relaxed">This is a transition-readiness model, not a classified sensor model. Values are illustrative and designed to show how SALAR can organize evidence for evaluator review.</p>
          </div>

          <div className="grid lg:grid-cols-[.85fr_1.15fr] gap-6">
            <div className="space-y-4">
              {loes.map((loe) => (
                <button
                  key={loe.id}
                  onClick={() => setActiveLoe(loe)}
                  className={`w-full text-left rounded-2xl border p-4 transition-all ${activeLoe.id === loe.id ? 'border-cyan-300 bg-cyan-400/12 shadow-lg shadow-cyan-950/30' : 'border-blue-500/20 bg-slate-950/35 hover:border-cyan-400/50'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-black text-cyan-300">{loe.label}</span>
                    <span className="text-xs text-blue-200">Baseline {loe.baseline} · SALAR {loe.advantage}</span>
                  </div>
                  <h3 className="mt-2 text-lg font-black">{loe.name}</h3>
                  <p className="mt-1 text-sm text-blue-100 leading-relaxed">{loe.subtitle}</p>
                </button>
              ))}
            </div>

            <div className="rounded-3xl border border-cyan-400/20 bg-[#0c1c31] p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
                <div>
                  <p className="text-sm font-black text-cyan-300">{activeLoe.label}</p>
                  <h3 className="text-2xl font-black">{activeLoe.name}</h3>
                  <p className="mt-2 text-sm text-blue-100 leading-relaxed">{activeLoe.mission}</p>
                </div>
                <button onClick={downloadReport} className="shrink-0 inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-300/40 px-3 py-2 text-sm font-bold text-cyan-100 hover:bg-cyan-400/10">
                  <Download size={16} /> PDF
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                {scenarios.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setScenario(item.id)}
                      className={`rounded-xl border p-3 text-left transition-colors ${scenario === item.id ? 'border-cyan-300 bg-cyan-400/10' : 'border-blue-500/20 bg-slate-950/35 hover:border-cyan-400/50'}`}
                    >
                      <Icon size={18} className="text-cyan-300 mb-2" />
                      <span className="text-sm font-bold">{item.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="grid md:grid-cols-3 gap-3 mb-5">
                <Slider label="Platform noise stress" value={noise} setValue={setNoise} low={0} high={100} />
                <Slider label="SWaP-C stress" value={swap} setValue={setSwap} low={0} high={100} />
                <Slider label="Provenance confidence" value={provenance} setValue={setProvenance} low={0} high={100} />
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mb-5">
                <StatBar label="Mission fit" value={score} icon={BarChart3} />
                <StatBar label="Transition evidence" value={transition} icon={Activity} />
                <StatBar label="Source confidence" value={provenance} icon={ShieldCheck} />
              </div>

              <div className="rounded-2xl border border-cyan-400/15 bg-slate-950/45 p-5">
                <h4 className="font-black text-cyan-300 mb-2">SALAR interpretation</h4>
                <p className="text-sm text-blue-100 leading-relaxed">{activeLoe.objective}</p>
                <p className="mt-3 text-sm text-blue-100 leading-relaxed">{activeLoe.salarEdge}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {activeLoe.riskDrivers.map((risk) => (
                    <span key={risk} className="rounded-full border border-amber-300/30 bg-amber-400/10 px-3 py-1 text-xs font-bold text-amber-200">
                      {risk}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-[#06111f] border-t border-cyan-400/20">
        <div className="container mx-auto px-5 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="rounded-3xl border border-cyan-400/20 bg-[#0c1c31] p-6">
              <Lock className="text-cyan-300 mb-4" size={28} />
              <h3 className="text-xl font-black mb-2">Confidentiality boundary</h3>
              <p className="text-sm text-blue-100 leading-relaxed">This page is public-safe. It uses synthetic values only and does not include classified information, CUI, GFI, export-controlled implementation detail, or non-public customer data.</p>
            </div>
            <div className="rounded-3xl border border-cyan-400/20 bg-[#0c1c31] p-6">
              <RefreshCw className="text-cyan-300 mb-4" size={28} />
              <h3 className="text-xl font-black mb-2">TRL realism</h3>
              <p className="text-sm text-blue-100 leading-relaxed">SALAR should claim TRL 4 for the software/evidence-model demo only after repeatable operation is verified. Physical sensor hardware maturity must be claimed only with lab or field evidence.</p>
            </div>
            <div className="rounded-3xl border border-cyan-400/20 bg-[#0c1c31] p-6">
              <AlertTriangle className="text-amber-300 mb-4" size={28} />
              <h3 className="text-xl font-black mb-2">No endorsement claim</h3>
              <p className="text-sm text-blue-100 leading-relaxed">The demo references the FARSEER mission area for capability alignment only. It does not imply government certification, selection, approval, or endorsement.</p>
            </div>
          </div>

          <div className="mt-8 rounded-3xl border border-blue-500/20 bg-slate-950/35 p-6">
            <h3 className="text-2xl font-black mb-4">Evidence pillars used in the demo</h3>
            <div className="grid md:grid-cols-4 gap-3">
              {pillars.map(([id, text]) => (
                <div key={id} className="rounded-2xl bg-[#0c1c31] border border-cyan-400/15 p-4">
                  <p className="text-cyan-300 font-black">{id}</p>
                  <p className="mt-2 text-sm text-blue-100 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
